<template>
  <div class="relative" ref="menuContainer">
    <button ref="accountMenuButton" type="button" aria-label="Open account menu" :aria-expanded="isOpen" @click="toggleMenu"
      class="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md cursor-pointer transition-all select-none">
      <!-- Avatar / Initial -->
      <div class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
        {{ userInitials }}
      </div>

      <!-- Name -->
      <span class="text-sm font-medium text-gray-700 hidden md:block">
        {{ userName }}
      </span>

      <i class="pi pi-angle-down text-xs text-gray-400 ml-1 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"></i>
    </button>

    <!-- Dropdown Menu -->
    <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
      <div v-if="isOpen"
        class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-2">
        <!-- User Info Header -->
        <div class="px-4 py-3 border-b border-gray-100 mb-1">
          <p class="text-sm font-semibold text-gray-800">{{ userName }}</p>
          <p class="text-xs text-gray-500 truncate">{{ userEmail }}</p>
          <p class="text-xs text-gray-400 mt-1 capitalize">{{ userRole }}</p>
        </div>

        <!-- Current School -->
        <div v-if="currentSchoolName" class="px-4 py-2 border-b border-gray-100 mb-1">
          <p class="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Current School</p>
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
              <i class="pi pi-building text-primary text-[10px]"></i>
            </div>
            <span class="text-sm font-medium text-gray-800 truncate">{{ currentSchoolName }}</span>
          </div>
        </div>

        <div v-if="hasGuardianPortal" class="border-b border-gray-100 mb-1">
          <Button label="Parent portal" icon="pi pi-users" text severity="secondary"
            class="!w-full !justify-start" data-testid="switch-to-parent-portal" @click="openParentPortal" />
        </div>

        <div v-if="hasMultipleSchools" class="border-b border-gray-100 mb-1">
          <Button label="Switch school" icon="pi pi-building" text severity="secondary"
            class="!w-full !justify-start" data-testid="open-school-switcher" @click="openSchoolSwitcher" />
        </div>

        <!-- Menu Items -->
        <div class="space-y-0.5 py-1">
          <button @click="handleProfile"
            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <i class="pi pi-user text-gray-400"></i>
            Profile
          </button>
          <button v-if="canViewSettings" @click="handleSettings"
            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <i class="pi pi-cog text-gray-400"></i>
            Settings
          </button>
        </div>

        <!-- Logout -->
        <div class="pt-1 border-t border-gray-100">
          <button @click="handleLogout"
            class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
            <i class="pi pi-sign-out text-red-500"></i>
            Logout
          </button>
        </div>
      </div>
    </transition>
    <Dialog v-model:visible="schoolSwitcherOpen" modal header="Switch school" @hide="accountMenuButton?.focus()"
      :style="{ width: '34rem', maxWidth: 'calc(100vw - 2rem)' }" :draggable="false">
      <SchoolPicker v-if="schoolSwitcherOpen" @cancel="schoolSwitcherOpen = false" />
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import SchoolPicker from '@/components/common/SchoolPicker.vue';
import { can, AppPermissions } from '@/utils/permissions';

const router = useRouter();
const userStore = useUserStore();
const isOpen = ref(false);
const schoolSwitcherOpen = ref(false);
const accountMenuButton = ref<HTMLButtonElement | null>(null);
const menuContainer = ref<HTMLElement | null>(null);

const currentUser = computed(() => userStore.currentUser || null);

const canViewSettings = computed(() =>
  can(userStore.currentUser, AppPermissions.SchoolProfile.Actions.View, AppPermissions.SchoolProfile.Resource),
);

const userName = computed(() => {
  if (!currentUser.value) return 'User';
  const user = currentUser.value as Record<string, unknown>;
  if (typeof user.name === 'string' && user.name) return user.name;
  if (typeof user.first_name === 'string' && typeof user.surname === 'string') return `${user.first_name} ${user.surname}`;
  return 'User';
});

const userInitials = computed(() => {
  const name = userName.value;
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

const userEmail = computed(() => (currentUser.value as Record<string, unknown> | null)?.email as string || '');
const userRole = computed(() => (currentUser.value as Record<string, unknown> | null)?.__typename as string || 'User');

// --- School switching ---
const currentSchoolName = computed(() => userStore.currentSchool?.name || '');
const hasMultipleSchools = computed(() => userStore.userSchools.length > 1);
function openSchoolSwitcher() {
  isOpen.value = false;
  schoolSwitcherOpen.value = true;
}

// Staff who are also guardians in this school open the parent portal here.
const hasGuardianPortal = computed(() => userStore.isGuardian);
function openParentPortal() {
  isOpen.value = false;
  router.push({ name: 'parent-home' });
}

function toggleMenu() {
  isOpen.value = !isOpen.value;
}

function closeMenu(e: MouseEvent) {
  if (menuContainer.value && !menuContainer.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
}

function handleProfile() {
  isOpen.value = false;
  router.push({ name: 'admin-my-account', query: { tab: 'profile' } });
}

function handleSettings() {
  isOpen.value = false;
  router.push({ name: 'admin-settings' });
}

function handleLogout() {
  isOpen.value = false;
  userStore.logout();
  router.push({ name: 'login' });
}

onMounted(() => {
  document.addEventListener('click', closeMenu);
  if (!userStore.currentUser) {
    userStore.getCurrentUser();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenu);
});
</script>
